import { COLORTYPES } from "../../../utils/colors";
import uniqBy from "lodash/uniqBy";
import sha1 from "js-sha1";
import base64 from "base-64";

const _ = {
  uniqBy: uniqBy,
};

const TextEditorBaseValues = {
  basicAmount: 6.99,
  pricePerSqm: 49,
  surchargeUnderglass: {
    fix: 0,
    percentage: 0,
  },
  title: {
    default: "Cutting Plotted Custom Design", // FIXME not used currently
    i18n: "editor.file.type.cuttingPlott.title", // FIXME not used currently
  },
  description: {
    default: "editor.file.type.cuttingPlott.description", // FIXME not used currently
    i18n: "editor.file.type.cuttingPlott.description", // FIXME not used currently
  },
  colors: [
    // FIXME not used currently
    COLORTYPES.GLOSSY,
    COLORTYPES.MATT,
    COLORTYPES.NEON,
    COLORTYPES.FROSTED,
  ],
  colorsTranslations: {
    1: "Glänzend",
    2: "Matt",
    3: "Neon",
    4: "Milchglas",
  },
  sections: [
    "size",
    "vinyl",
    // 'color',
    "underglass",
    "lines",
  ],
  discounts: [
    { amount: 2, percentage: 10 },
    { amount: 5, percentage: 15 },
    { amount: 10, percentage: 20 },
    { amount: 20, percentage: 30 },
    { amount: 30, percentage: 40 },
    { amount: 50, percentage: 50 },
    { amount: 100, percentage: 60 },
    { amount: 250, percentage: 70 },
    { amount: 500, percentage: 77 },
  ],
};

const extractFont = (line) => {
  let font = {};
  if (line.font) {
    font = { ...line.font.value };
  }
  font.bold = line.bold || false;
  font.italic = line.italic || false;
  font.fontSize = line.fontSize;
  font.fontFamily = line.font && line.font.value ? line.font.value.title : "";
  font.loaded = true;
  font.loading = false;
  font.missingCharacters = [];

  return font;
};

const extractFormState = (formState) => {
  if (formState) {
    let newLineSettings = [];
    if (formState.lines) {
      newLineSettings = formState.lines.map((setting) => {
        let returnVal = {};
        if (setting.color) returnVal.color = setting.color.value;
        if (setting.font) returnVal.font = setting.font.value;
        if (setting.bold) returnVal.bold = setting.bold;
        if (setting.italic) returnVal.italic = setting.italic;
        if (setting.allignment) returnVal.allignment = setting.allignment;
        if (setting.fontSize) returnVal.fontSize = setting.fontSize;
        if (setting.lineHeight) returnVal.lineHeight = setting.lineHeight;
        if (setting.text) returnVal.text = setting.text;
        return returnVal;
      });
    }

    let svgJson = {};
    svgJson.width =
      formState.baseDimension.type === "width"
        ? formState.baseDimension.size
        : formState.calculatedDimension.size;
    svgJson.height =
      formState.baseDimension.type === "height"
        ? formState.baseDimension.size
        : formState.calculatedDimension.size;
    svgJson.baseDimension = formState.baseDimension.type;
    svgJson.underglass = formState.underglass;
    svgJson.amount = formState.amount;
    svgJson.materialSerie = formState.materialSerie;
    svgJson.lines = formState.lines.map((line) => ({
      text: line.text,
      textAnchor: line.allignment,
      color: line.color ? line.color.value : {},
      font: extractFont(line),
      lineHeight: line.lineHeight,
      computedLineHeight: line.computedLineHeight,
      computedLineWidth: line.computedLineWidth,
    }));

    return {
      materialSerie: formState.materialSerie,
      text: formState.text,
      lines: newLineSettings,
      fontUpload: formState.fontUpload,
      size: formState.size,
      baseDimension: formState.baseDimension,
      calculatedDimension: formState.calculatedDimension,
      underglass: formState.underglass,
      negative: formState.negative,
      svgJson,
    };
  } else {
    return {};
  }
};

function getFoilBasePrice(svgJson) {
  let price = 49; // fallback value
  // get used colors
  let highestPrice = 0;
  svgJson.lines &&
    svgJson.lines.forEach((line) => {
      if (line.color.pricePerSqm > highestPrice) {
        highestPrice = line.color.pricePerSqm;
      }
    });
  price = highestPrice > 0 ? highestPrice : price;
  return price;
}

function getSurfaceSizeForPrice(svgJson) {
  const { width, height } = svgJson;
  // convert mm to cm (/10), convert sqCM to sqM (/10000)
  // return ((width / 10 + border.border) * (height / 10 + border.border)) / 10000;
  return ((width / 10) * (height / 10)) / 10000;
}

function getUnderglassSurcharge(svgJson, basisPrice) {
  const { surchargeUnderglass } = TextEditorBaseValues;
  let surcharge = 0;
  if (svgJson.underglass) {
    surcharge =
      surchargeUnderglass.fix +
      (basisPrice * surchargeUnderglass.percentage) / 100;
  }
  return surcharge;
}

function getLinesSurcharge(svgJson, basisPrice) {
  const surchargePerLetter = 0.05; // 5ct surcharge per letter
  let surcharge = 0;
  const usedColors = [];
  svgJson.lines &&
    svgJson.lines.forEach((line) => {
      if (line.text) surcharge += line.text.length * surchargePerLetter;
      line.color && usedColors.push(line.color.farbcode);
    });
  if (_.uniqBy(usedColors).length > 1) {
    surcharge += basisPrice * (_.uniqBy(usedColors).length / 1.1 - 1);
  }

  return surcharge;
}

const calculatePrice = (svgJson) => {
  let price = 0;
  const editorBasePrice = TextEditorBaseValues.basicAmount;

  const surfaceSize = getSurfaceSizeForPrice(svgJson);
  const foilBasePrice =
    getFoilBasePrice(svgJson) *
    1.4 *
    (1 - (surfaceSize > 0.05 ? (surfaceSize > 1 ? 0.2 : surfaceSize / 5) : 0));
  price += editorBasePrice + foilBasePrice * surfaceSize;

  if (svgJson.underglass) {
    price += getUnderglassSurcharge(svgJson, price);
  }
  price += getLinesSurcharge(svgJson, price);
  // price += price;
  // let maxDiscount = {};
  let currentDiscount = {};
  if (TextEditorBaseValues.discounts) {
    TextEditorBaseValues.discounts.forEach((discount) => {
      if (discount.amount <= svgJson.amount) {
        currentDiscount = discount;
      }
    });
    // maxDiscount = TextEditorBaseValues.discounts[TextEditorBaseValues.discounts.length - 1];
  }
  // 50% off if only one letter
  if (
    svgJson.lines &&
    svgJson.lines.length === 1 &&
    svgJson.lines[0].text.length < 2
  )
    price = price * 0.4536;

  if (surfaceSize < 0.002) {
    price = price * 1.15;
  } else if (surfaceSize < 0.005) {
    price = price * 1.1;
  } else {
    price = price * 1.05;
  }
  if (price < 4.16) price = 4.16; // 4.95

  const priceBeforeDiscountAndTax = price + 0;
  const displayPrice =
    price -
    (currentDiscount.percentage
      ? (price * currentDiscount.percentage) / 100
      : 0);
  const Vat = Math.round(displayPrice * 0.19 * 100) / 100;
  const finalPrice = Math.round((displayPrice + Vat) * 100) / 100;
  return { displayPrice, finalPrice, Vat, priceBeforeDiscountAndTax };
};

function uniqueId(length) {
  let id;
  id = "";
  while (id.length < length) {
    id += Math.random().toString(36).substr(2);
  }
  return id.substr(0, length);
}

function generateSKU() {
  const slug = "text";
  return slug + "_" + uniqueId(25);
}

function generateChecksum() {
  const checksum = sha1("hash me");
  return checksum;
}

function generateDescriptionText(state, svgJson, translations) {
  // console.log('svgjson', svgJson)
  // 460mm x 443mm | Typ: Motiv als Digitaldruck auf Klebefolie | Schutzlaminat: Glänzend | Hinterglasverklebung: Nein | Form: Rechteckig | Rand: +0cm
  let description = "";
  const underglassVal = state.underglass ? translations.yes : translations.no;

  TextEditorBaseValues.sections.forEach((section) => {
    switch (section) {
      case "size":
        description += `${translations.width}: ${Math.floor(
          svgJson.width
        )}mm (${(svgJson.width / 10).toFixed(1)}cm) x`;
        description += `${translations.height}: ${Math.floor(
          svgJson.height
        )}mm (${(svgJson.height / 10).toFixed(1)}cm) | `;
        break;
      case "vinyl":
        description +=
          translations.material +
          ": " +
          TextEditorBaseValues.colorsTranslations[state.materialSerie] +
          " | ";
        break;
      // case 'color':
      //   const selectedColor = svgJson.lines[0].color;
      //   description += translations.color + ': ' + selectedColor.title + '(' + selectedColor.farbcode + '_' + state.materialSerie + ')' + ' | ';
      //   break;
      case "underglass":
        if (state.underglass) {
          description += translations.underglass + ": " + underglassVal + " | ";
        } // Users are confused if this is not selected
        break;
      case "lines":
        description += translations.lines + ": ";
        svgJson.lines.forEach((line, idx) => {
          description += `\n\r#${idx}: ${line.text} | ${translations.color}: ${line.color.title}(${line.color.farbcode}_${svgJson.materialSerie}), ${translations.font}: ${line.font.fontFamily} | `;
        });
        break;
      default:
        description += translations.missingSection + section + " | ";
        break;
    }
  });

  return description.slice(0, -3);
}

function generateSVG() {
  const svgElem = document.getElementById("text-svg");
  let b64 = null;
  if (svgElem) {
    svgElem.setAttribute("version", "1.1");
    svgElem.setAttribute("myfolie", "editor");
    let svgHtml = svgElem.outerHTML;
    if (!svgHtml) {
      svgHtml = svgElem.parentNode.innerHTML;
    }
    b64 = base64.encode(svgHtml); // or use btoa if supported
  }
  return b64;
}

function generateLines(lines) {
  return lines.map((line, idx) => {
    return {
      color: line.color,
      text: line.text,
      id: idx,
      font: line.font,
    };
  });
}

const generateJson = (state, svgJson, lang, translations) => {
  const calculatedPrice = calculatePrice(svgJson);
  return {
    width: svgJson.width,
    amount: svgJson.amount || 1,
    height: svgJson.height,
    origin: lang,
    productTitle: translations.title,
    description: generateDescriptionText(state, svgJson, translations),
    underglass: svgJson.underglass || false,
    vinyl: state.materialSerie,
    lines: generateLines(svgJson.lines),
    editorType: 6,
    // preview: state.file.preview,
    sku: generateSKU(),
    // color: svgJson.lines[0].color.farbcode,
    // filecheck: state.filecheck.filecheck,
    // vectorizeFile: state.vectorizeFilecheck.vectorizeFilecheck,
    price: calculatedPrice["finalPrice"],
    priceBeforeDiscountAndTax: calculatedPrice["priceBeforeDiscountAndTax"],
    depth: generateChecksum(),
    svg: generateSVG(),
  };
};

const formHelpers = {
  extractFormState: extractFormState,
  calculatePrice: calculatePrice,
  generateJson: generateJson,
  generateSKU: generateSKU,
};

export default formHelpers;
